/**
 * Slide sources on a slides activity (content.decks).
 * Mirror of constants/slideDecks.js — one 20-slide unit study, lessons .1–.4
 * take 1–5 / 6–10 / 11–15 / 16–20.
 */

export const SLIDE_KINDS = ["canva", "pdf", "pptx"] as const;
export type SlideKind = (typeof SLIDE_KINDS)[number];

export const SLIDE_MODES = ["activity", "map"] as const;
export type SlideMode = (typeof SLIDE_MODES)[number];

export const MAP_TARGET_KINDS = ["course", "container", "item"] as const;
export type MapTargetKind = (typeof MAP_TARGET_KINDS)[number];

export type OutlineMark = "C" | "U" | "L" | "I";

export type OutlinePick = {
  kind: MapTargetKind;
  id: string;
  label: string;
  mark?: OutlineMark;
};

export type OutlineNode = {
  id: string;
  kind: MapTargetKind;
  mark: OutlineMark;
  label: string;
  title: string;
  children: OutlineNode[];
};

export type SlideMap = {
  id: string;
  from: number;
  to: number;
  targetKind: MapTargetKind;
  targetId: string;
  targetLabel?: string;
  targets?: OutlinePick[];
};

export type SlideDeck = {
  id: string;
  title: string;
  kind: SlideKind;
  url: string;
  mediaId?: string;
  slideCount?: number;
  maps: SlideMap[];
};

export type OutlineTarget = {
  id: string;
  kind: MapTargetKind;
  label: string;
};

export const UNIT_LESSON_BLOCKS_20: Array<{ from: number; to: number }> = [
  { from: 1, to: 5 },
  { from: 6, to: 10 },
  { from: 11, to: 15 },
  { from: 16, to: 20 },
];

export const SLIDE_FILE_ACCEPT = ".pdf,application/pdf";

export function newDeckId() {
  return `d-${Math.random().toString(36).slice(2, 10)}`;
}

export function emptySlideDeck(): SlideDeck {
  return {
    id: newDeckId(),
    title: "",
    kind: "canva",
    url: "",
    slideCount: 20,
    maps: [],
  };
}

export type CanvaParse = {
  id: string;
  view: string;
  embed: string;
  token?: string;
  short?: string;
};

function canvaHost(hostname: string) {
  return hostname.replace(/^www\./i, "").toLowerCase();
}

function extractCanvaCandidate(raw: string) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const attr = text.match(/(?:src|href)\s*=\s*["']([^"']+)["']/i);
  if (attr && /canva\.(com|link)/i.test(attr[1])) return attr[1].trim();
  const dataId = text.match(/data-design-id\s*=\s*["']([^"']+)["']/i);
  if (dataId) return `https://www.canva.com/design/${dataId[1]}/view`;
  return text;
}

const CANVA_ID = /^[A-Za-z0-9_-]+$/;
const CANVA_MODES = new Set(["view", "edit", "watch", "present"]);

export function isCanvaShortLink(raw: string | null | undefined) {
  const text = extractCanvaCandidate(String(raw || ""));
  if (!text) return false;
  try {
    const url = new URL(text);
    const host = canvaHost(url.hostname);
    const slug = url.pathname.replace(/^\/+|\/+$/g, "");
    return host === "canva.link" && Boolean(slug) && !slug.includes("/");
  } catch {
    return false;
  }
}

export function parseCanvaUrl(raw: string | null | undefined): CanvaParse | null {
  const text = extractCanvaCandidate(String(raw || ""));
  if (!text) return null;
  try {
    const url = new URL(text);
    const host = canvaHost(url.hostname);
    if (host === "canva.link") {
      const slug = url.pathname.replace(/^\/+|\/+$/g, "");
      if (!slug || slug.includes("/")) return null;
      return { id: slug, view: "", embed: "", short: `https://canva.link/${slug}` };
    }
    if (host !== "canva.com") return null;
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts[0]?.toLowerCase() !== "design" || !parts[1]) return null;
    const id = parts[1];
    if (!CANVA_ID.test(id)) return null;

    let token = "";
    let mode = "view";
    if (parts.length === 3) {
      const third = parts[2];
      if (CANVA_MODES.has(third.toLowerCase())) mode = third.toLowerCase();
      else if (CANVA_ID.test(third)) token = third;
      else return null;
    } else if (parts.length >= 4) {
      token = parts[2];
      mode = String(parts[3] || "view").toLowerCase();
      if (!CANVA_ID.test(token)) return null;
    }

    if (mode === "edit" && !token) return null;
    const path = token ? `/design/${id}/${token}/view` : `/design/${id}/view`;
    const view = `https://www.canva.com${path}`;
    return { id, view, embed: `${view}?embed`, ...(token ? { token } : {}) };
  } catch {
    return null;
  }
}

export function canvaEmbedUrl(raw: string) {
  return parseCanvaUrl(raw)?.embed || "";
}

export function deckHasCanvaSource(url: string | null | undefined) {
  const parsed = parseCanvaUrl(url);
  return Boolean(parsed?.embed || parsed?.short);
}

function inferKind(raw: { kind?: unknown; url?: unknown }): SlideKind {
  const url = String(raw.url || "").trim();
  const kind = String(raw.kind || "").trim();
  if ((SLIDE_KINDS as readonly string[]).includes(kind)) return kind as SlideKind;
  if (parseCanvaUrl(url)) return "canva";
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".ppt") || path.endsWith(".pptx")) return "pptx";
  return "pdf";
}

function clampSlide(value: unknown, fallback: number) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.min(999, Math.round(n)));
}

function asMark(value: unknown): OutlineMark | undefined {
  return value === "C" || value === "U" || value === "L" || value === "I" ? value : undefined;
}

function normalizePick(raw: unknown): OutlinePick | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const kindRaw = row.kind || row.targetKind;
  const kind = MAP_TARGET_KINDS.includes(kindRaw as MapTargetKind) ? (kindRaw as MapTargetKind) : null;
  const id = String(row.id || row.targetId || "").trim();
  if (!kind || !id) return null;
  const label = String(row.label || row.targetLabel || "").trim();
  const mark = asMark(row.mark);
  return { kind, id, label, ...(mark ? { mark } : {}) };
}

function normalizeSlideMap(raw: unknown): SlideMap | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const seen = new Set<string>();
  const picks: OutlinePick[] = [];
  const add = (pick: OutlinePick | null) => {
    if (!pick) return;
    const key = `${pick.kind}:${pick.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    picks.push(pick);
  };
  if (Array.isArray(row.targets)) row.targets.forEach((entry) => add(normalizePick(entry)));
  if (!picks.length) {
    add(
      normalizePick({
        kind: row.targetKind,
        id: row.targetId,
        label: row.targetLabel,
        mark: row.mark,
      }),
    );
  }
  if (!picks.length) return null;
  const first = picks[0];
  const from = clampSlide(row.from, 1);
  const to = clampSlide(row.to, from);
  const targetLabel = String(
    picks.map((p) => p.label).filter(Boolean).join(" · ") || row.targetLabel || "",
  ).trim();
  return {
    id: String(row.id || "").trim() || newDeckId(),
    from,
    to: Math.max(from, to),
    targetKind: first.kind,
    targetId: first.id,
    ...(targetLabel ? { targetLabel } : {}),
    ...(picks.length > 1 || first.mark ? { targets: picks } : {}),
  };
}

export function outlineKey(kind: string, id: string) {
  return `${kind}:${id}`;
}

export function pickLabel(node: OutlineNode) {
  if (node.kind === "course") return node.title;
  if (node.label && node.title && node.title !== node.label) return `${node.label} · ${node.title}`;
  return node.title || node.label;
}

export function findOutlineNode(nodes: OutlineNode[], kind: string, id: string): OutlineNode | null {
  for (const node of nodes) {
    if (node.kind === kind && node.id === id) return node;
    const nested = findOutlineNode(node.children, kind, id);
    if (nested) return nested;
  }
  return null;
}

export function picksForMap(map: SlideMap, outline: OutlineNode[]): OutlinePick[] {
  const raw = map.targets?.length
    ? map.targets
    : map.targetId
      ? [{ kind: map.targetKind, id: map.targetId, label: map.targetLabel || "" }]
      : [];
  return raw.map((pick) => {
    const node = findOutlineNode(outline, pick.kind, pick.id);
    if (!node) return pick;
    return { kind: node.kind, id: node.id, label: pick.label || pickLabel(node), mark: node.mark };
  });
}

export function applyPicks(map: SlideMap, picks: OutlinePick[]): SlideMap {
  const first = picks[0];
  return {
    ...map,
    targetKind: first?.kind || "container",
    targetId: first?.id || "",
    targetLabel: picks.map((p) => p.label).filter(Boolean).join(" · ") || undefined,
    targets: picks,
  };
}

export function normalizeSlideDeck(raw: unknown): SlideDeck {
  if (!raw || typeof raw !== "object") return emptySlideDeck();
  const row = raw as Record<string, unknown>;
  const kind = inferKind(row);
  const url = String(row.url || "").trim();
  const mediaId = String(row.mediaId || "").trim();
  const count = Number(row.slideCount);
  const canva = kind === "canva" ? parseCanvaUrl(url) : null;
  return {
    id: String(row.id || "").trim() || newDeckId(),
    title: String(row.title || "").trim(),
    kind,
    url: kind === "canva" ? canva?.view || url : url,
    ...(mediaId ? { mediaId } : {}),
    ...(Number.isFinite(count) && count > 0 ? { slideCount: Math.round(count) } : {}),
    maps: Array.isArray(row.maps) ? row.maps.map(normalizeSlideMap).filter((m): m is SlideMap => Boolean(m)) : [],
  };
}

export function normalizeSlideDecks(list: unknown): SlideDeck[] {
  return Array.isArray(list) ? list.map(normalizeSlideDeck) : [];
}

export function normalizeSlideMode(raw: unknown): SlideMode | null {
  return raw === "activity" || raw === "map" ? raw : null;
}

export function inferSlideMode(raw: unknown, decks: SlideDeck[]): SlideMode | null {
  const explicit = normalizeSlideMode(raw);
  if (explicit) return explicit;
  if (decks.some((deck) => deck.maps.some((map) => map.targetId || map.targets?.length))) return "map";
  if (decks.some((deck) => deckHasSource(deck))) return "activity";
  return null;
}

export function deckHasSource(deck: SlideDeck | null | undefined) {
  if (!deck) return false;
  if (deck.kind === "canva") return deckHasCanvaSource(deck.url);
  return Boolean(String(deck.mediaId || "").trim() || String(deck.url || "").trim());
}

export function unitLessonBlocks(slideCount?: number) {
  const n = Number(slideCount);
  const total = Number.isFinite(n) && n > 0 ? Math.round(n) : 20;
  if (total === 20) return UNIT_LESSON_BLOCKS_20.map((row) => ({ ...row }));
  const size = Math.max(1, Math.floor(total / 4));
  return [0, 1, 2, 3].map((i) => ({
    from: i * size + 1,
    to: i === 3 ? total : (i + 1) * size,
  }));
}

export function mapsForLessons(
  lessons: Array<{ id: string; title?: string | null; label?: string }>,
  slideCount?: number,
): SlideMap[] {
  const blocks = unitLessonBlocks(slideCount);
  return lessons.slice(0, 4).map((lesson, index) => ({
    id: newDeckId(),
    from: blocks[index].from,
    to: blocks[index].to,
    targetKind: "container" as const,
    targetId: lesson.id,
    targetLabel: `${lesson.label || "Lesson"} · ${lesson.title?.trim() || "Untitled"}`,
  }));
}

export function pdfViewerUrl(url: string, page?: number) {
  const base = String(url || "").trim();
  if (!base) return "";
  const pageNum = Number(page);
  const hash = Number.isFinite(pageNum) && pageNum > 1 ? `#page=${Math.round(pageNum)}` : "";
  return `${base.replace(/#.*$/, "")}${hash}`;
}

export function kindLabel(kind: SlideKind) {
  if (kind === "canva") return "Canva";
  if (kind === "pptx") return "PowerPoint";
  return "PDF";
}
